import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import SectionHeader from "../../User/Profile/SectionHeader";
import InputField from "../../User/Profile/InputField";
import useUsers from "../Users/Hook/useUsers";

import {
    SET_PROFILE_AUTH
} from "../../../redux/action/auth/type";

const AdminProfileContent = () => {

    const dispatch = useDispatch();
    const profile = useSelector(state => state.auth.profile);
    const { updateUser } = useUsers();

    const toastLock = useRef(false);

    const showError = (message) => {
        if (toastLock.current) return;
        toastLock.current = true;
        toast.error(message);
        setTimeout(() => {
            toastLock.current = false;
        }, 1500);
    };

    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        phone: ""
    });

    const [firstNameState, setFirstNameState] = useState("");
    const [lastNameState, setLastNameState] = useState("");
    const [phoneState, setPhoneState] = useState("");

    useEffect(() => {
        setFirstNameState(profile?.firstName || "درحال دریافت نام ...");
        setLastNameState(profile?.lastName || "درحال دریافت نام خانوادگی ...");
        setPhoneState(profile?.phone || "درحال دریافت شماره تماس شما ...");

        setForm({
            firstName: profile?.firstName || "",
            lastName: profile?.lastName || "",
            phone: profile?.phone || ""
        });
    }, [profile]);

    const [loading, setLoading] = useState(false);

    // تبدیل اعداد فارسی و عربی به انگلیسی
    const convertToEnglishDigits = (value) => {
        const persian = "۰۱۲۳۴۵۶۷۸۹";
        const arabic = "٠١٢٣٤٥٦٧٨٩";

        return value
            .replace(/[۰-۹]/g, d => persian.indexOf(d))   // فارسی → انگلیسی
            .replace(/[٠-٩]/g, d => arabic.indexOf(d));   // عربی → انگلیسی
    };


    // اعتبارسنجی نام فارسی
   const validatePersianName = (name, fieldName = "نام") => {
        if (!/^[آ-ی\s]+$/.test(name.trim())) {
            toast.error(`${fieldName} فقط باید شامل حروف فارسی باشد`, {
                className: "z-[10001]"
            });
            return false;
        }

        return true;
    };

    const validatePhone = (phone) => {

        if (!/^09\d{9}$/.test(phone)) {
            toast.error("شماره موبایل نامعتبر است", {
                className: "z-[10001]"
            });
            return false;
        }

        return true;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "firstName" || name === "lastName") {

            if (!/^[\u0600-\u06FF\s]*$/.test(value)) {
                showError("نام معتبر نیست، فقط حروف فارسی مجاز است");
                return;
            }

            setForm(prev => ({ ...prev, [name]: value }));

            if (name === "firstName"){
                
                let value = e.target.value;

                value = value.replace(/[^آ-ی\s]/g, "");

                setFirstNameState(value);

            }
            if (name === "lastName"){
                
                let value = e.target.value;

                value = value.replace(/[^آ-ی\s]/g, "");

                setLastNameState(value);
            }

            return;
        }

        if (name === "phone") {

            let converted = convertToEnglishDigits(value);
            converted = converted.replace(/\D/g, "");
            converted = converted.slice(0, 11);

            setForm(prev => ({ ...prev, phone: converted }));
            setPhoneState(converted);

            return;
        }

        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateProfile = async () => {

        const newFirstName = firstNameState.trim();
        const newLastName = lastNameState.trim();
        const newPhone = phoneState.trim();

        if (!validatePersianName(newFirstName) || !validatePersianName(newLastName)) {
            showError("نام معتبر نیست، فقط باید فارسی باشد");
            return;
        }

        if (!validatePhone(newPhone)) {
            showError("شماره موبایل معتبر نیست و باید با 09 شروع شود");
            return;
        }

        if (!profile?._id) {
            showError("شناسه کاربر یافت نشد");
            return;
        }

        setLoading(true);

        try {
            const data = {
                firstName: newFirstName,
                lastName: newLastName,
                phone: newPhone
            };

            await updateUser(profile._id, data);

            const updatedProfile = {
                ...profile,
                ...data
            };

            dispatch({
                type: SET_PROFILE_AUTH,
                payload: updatedProfile
            });

            setFirstNameState(newFirstName);
            setLastNameState(newLastName);
            setPhoneState(newPhone);

            setForm(data);

        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto">

            <div className="flex flex-col items-center mb-10">

                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#543389] to-[#8b6bd9] flex items-center justify-center text-white text-3xl font-bold shadow-xl mb-4 border-4 border-white">
                    {(profile?.firstName?.[0] || "S")}
                    {(profile?.lastName?.[0] || "A")}
                </div>

                <h2 className="text-xl font-bold text-slate-800">
                    {firstNameState} {lastNameState}
                </h2>

                <span className="mt-1 px-3 py-1 bg-purple-100 text-[#543389] text-xs font-bold rounded-full">
                    {profile?.role === "admin" ? "مدیر سیستم" : profile?.role === "staff" ? 'نیروی سالن' : "کاربر"}
                </span>

            </div>

            <div className="space-y-6">

                <SectionHeader title="اطلاعات فردی" icon="👤" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    <InputField
                        label="نام"
                        name="firstName"
                        value={firstNameState}
                        placeholder="درحال دریافت نام ..."
                        onChange={handleChange}
                    />

                    <InputField
                        label="نام خانوادگی"
                        name="lastName"
                        value={lastNameState}
                        placeholder="درحال دریافت نام خانوادگی ..."
                        onChange={handleChange}
                    />

                </div>

                <div className="pt-4 border-t border-slate-100">

                    <SectionHeader title="امنیت و تماس" icon="🔒" />

                    <div className="relative">

                        <InputField
                            label="شماره موبایل"
                            name="phone"
                            value={phoneState}
                            placeholder="درحال دریافت شماره موبایل ..."
                            onChange={handleChange}
                        />

                    </div>

                </div>

                <div className="pt-8">

                    <button
                        onClick={handleUpdateProfile}
                        disabled={loading}
                        className="w-full h-12 rounded-xl bg-gradient-to-r from-[#543389] to-[#8b6bd9] text-white font-bold shadow-lg hover:scale-[1.01] transition flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : "ذخیره تغییرات"}
                    </button>

                </div>

            </div>

        </div>
    );
};

export default AdminProfileContent;